import { useRef, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { currentSessionLogout } from '@/router/portalRegistry'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronDown, ChevronRight, Home, LogOut, Package, RefreshCw, User } from 'lucide-react'
import { cn } from '@utils'
import { useIsCompact } from '@hooks/useDevice'
import type { NavigationalBlocksData } from '@/types'
import { useOmpNav } from '../hooks/useOmpNav'
import { useCrossSellCartVisible } from '../hooks/useCrossSellCart'
import OmpCrossSellCartButton from '../components/OmpCrossSellCartButton'

interface OmpNavigationalBlocksProps {
  labels: Required<NavigationalBlocksData>
  layout?: 'sidebar' | 'top'
  greeting?: ReactNode
}

type NavItem = {
  key: string
  label: string
  to: string
  icon: typeof Home
  matchPrefix: string
}

const OmpNavigationalBlocks = ({ labels, layout = 'sidebar', greeting }: OmpNavigationalBlocksProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const closedByPointer = useRef(false)
  const { navigateTo } = useOmpNav()
  const isCompact = useIsCompact()
  // The cross-sell cart's way back in; absent until something is in the cart.
  const showCart = useCrossSellCartVisible()

  // show_icons never applies to Log Out — it keeps its icon in every configuration.
  const showIcons = labels.show_icons !== false

  const allItems: NavItem[] = [
    { key: 'home', label: labels.home_label, to: '/portal', icon: Home, matchPrefix: '/portal' },
    { key: 'orders', label: labels.orders_label, to: '/portal/orders', icon: Package, matchPrefix: '/portal/orders' },
    {
      key: 'subscriptions',
      label: labels.subscriptions_label,
      to: '/portal/subscriptions',
      icon: RefreshCw,
      matchPrefix: '/portal/subscriptions',
    },
    {
      key: 'account',
      label: labels.address_payment_label,
      to: '/portal/account',
      icon: User,
      matchPrefix: '/portal/account',
    },
  ]
  const items = allItems.filter(item => item.key !== 'account' || labels.show_address_payment !== false)

  const isActive = (item: NavItem) => {
    if (item.matchPrefix === '/portal') {
      return location.pathname === '/portal' || location.pathname === '/portal/'
    }
    return location.pathname.startsWith(item.matchPrefix)
  }

  // Compact: a select-style control that opens one menu panel flush beneath it. The current
  // page is the trigger, so it's dropped from the list rather than repeated. Radix owns the
  // open state, focus trap and dismiss behaviour.
  if (isCompact) {
    const active = items.find(isActive) ?? items[0]
    const ActiveIcon = active.icon
    return (
      <nav className={cn('omp-nav-mobile', showCart && 'omp-nav-mobile-with-cart')} aria-label="Portal navigation">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger className="omp-nav-select" data-omp-nav="">
            {showIcons && <ActiveIcon className="omp-nav-select-icon" size={22} strokeWidth={1.75} />}
            <span className={cn('omp-nav-select-label', !!greeting && 'omp-nav-select-label-fit')}>{active.label}</span>
            {greeting && <span className="omp-nav-select-greeting">{greeting}</span>}
            <ChevronDown className="omp-nav-select-chevron" size={18} />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="omp-nav-menu" align="start" sideOffset={0} collisionPadding={16}>
              {items
                .filter(item => item.key !== active.key)
                .map(item => {
                  const Icon = item.icon
                  return (
                    <DropdownMenu.Item
                      key={item.key}
                      className="omp-nav-menu-item"
                      data-omp-nav=""
                      onSelect={() => navigateTo(item.to)}>
                      {showIcons && <Icon className="omp-nav-menu-icon" size={22} strokeWidth={1.75} />}
                      <span>{item.label}</span>
                    </DropdownMenu.Item>
                  )
                })}
              <DropdownMenu.Item className="omp-nav-menu-item" onSelect={() => navigate(currentSessionLogout())}>
                <LogOut className="omp-nav-menu-icon" size={22} strokeWidth={1.75} />
                <span>{labels.log_out_label}</span>
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
        {showCart && <OmpCrossSellCartButton variant="compact" />}
      </nav>
    )
  }

  // Top layout — horizontal menu that merges into the header.
  if (layout === 'top') {
    const endItem = greeting ? (
      // Non-modal: a modal menu locks scroll by hiding the scrollbar, which shifts the header.
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger className="omp-topnav-item omp-topnav-greeting">
          {greeting}
          <span className="omp-topnav-user-icon" aria-hidden="true" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className="omp-topnav-menu"
            align="end"
            sideOffset={8}
            collisionPadding={16}
            onPointerDownOutside={() => {
              closedByPointer.current = true
            }}
            onCloseAutoFocus={event => {
              if (closedByPointer.current) {
                event.preventDefault()
                closedByPointer.current = false
              }
            }}>
            <DropdownMenu.Item className="omp-topnav-menu-item" onSelect={() => navigate(currentSessionLogout())}>
              <LogOut size={20} strokeWidth={1.75} />
              <span>{labels.log_out_label}</span>
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    ) : (
      <button
        type="button"
        className="omp-topnav-item omp-topnav-logout"
        onClick={() => navigate(currentSessionLogout())}>
        <LogOut className="omp-topnav-icon" size={20} strokeWidth={1.75} />
        <span>{labels.log_out_label}</span>
      </button>
    )

    return (
      <nav className="omp-topnav" aria-label="Portal navigation">
        <div className="omp-topnav-items">
          {items.map(item => {
            const Icon = item.icon
            return (
              <button
                key={item.key}
                type="button"
                className={cn('omp-topnav-item', isActive(item) && 'omp-topnav-item-active')}
                data-omp-nav=""
                onClick={() => navigateTo(item.to)}>
                {showIcons && <Icon className="omp-topnav-icon" size={20} strokeWidth={1.75} />}
                <span>{item.label}</span>
              </button>
            )
          })}
        </div>
        {showCart ? (
          // Only with a cart: it shares the right-hand column with Log out / the greeting, just before it.
          <div className="omp-topnav-end">
            <OmpCrossSellCartButton variant="top" />
            {endItem}
          </div>
        ) : (
          endItem
        )}
      </nav>
    )
  }

  return (
    <nav className="omp-nav" aria-label="Portal navigation">
      {items.map(item => {
        const Icon = item.icon
        return (
          <button
            key={item.key}
            type="button"
            className={cn('omp-nav-block', isActive(item) && 'omp-nav-block-active')}
            data-omp-nav=""
            onClick={() => navigateTo(item.to)}>
            {showIcons && <Icon className="omp-nav-icon" size={22} strokeWidth={1.75} />}
            <span className="omp-nav-label">{item.label}</span>
            <ChevronRight className="omp-nav-chevron" size={20} />
          </button>
        )
      })}
      {showCart && <OmpCrossSellCartButton variant="sidebar" />}
      <button type="button" className="omp-nav-block omp-nav-logout" onClick={() => navigate(currentSessionLogout())}>
        <LogOut className="omp-nav-icon" size={22} strokeWidth={1.75} />
        <span className="omp-nav-label">{labels.log_out_label}</span>
      </button>
    </nav>
  )
}

export default OmpNavigationalBlocks
